'use client'

import { useEffect, useRef, useState } from 'react'
import {
  Bell,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { usePWAInstall } from '@/hooks/usePWAInstall'
import {
  enablePushNotifications,
} from '@/lib/firebase/push.client'

export function PushPermissionPrompt({
  userId,
  sellerId,
}: {
  userId: number | null
  sellerId?: number | null
}) {
  const { isInstalled } =
    usePWAInstall()

  const [open, setOpen] =
    useState(false)

  const [enabling, setEnabling] =
    useState(false)

  const registrationSyncStarted =
    useRef(false)

  useEffect(() => {
    if (
        !isInstalled ||
        !userId ||
        typeof Notification === 'undefined'
    ) {
        return
    }

    /*
    * Si el usuario YA dio permiso anteriormente,
    * sincronizamos Firebase automáticamente.
    *
    * No mostramos ningún modal.
    */
    if (
        Notification.permission === 'granted'
    ) {
        if (registrationSyncStarted.current) {
        return
        }

        registrationSyncStarted.current = true

        ;(async () => {
        try {
            console.log(
            '[push] syncing existing permission'
            )

            const result =
            await enablePushNotifications({
                userId,
                sellerId,
            })

            console.log(
            '[push] sync result',
            result
            )
        } catch (error) {
            console.error(
            '[push] sync failed',
            error
            )
        }
        })()

        return
    }

    /*
    * Si previamente dijo NO,
    * no podemos volver a pedir permiso.
    */
    if (
        Notification.permission === 'denied'
    ) {
        return
    }

    /*
    * Solo mostramos nuestro modal bonito
    * cuando todavía no ha decidido.
    */
    const timer =
        window.setTimeout(() => {
        setOpen(true)
        }, 1200)

    return () => {
        window.clearTimeout(timer)
    }
    }, [
    isInstalled,
    userId,
    sellerId,
    ])

  if (!open) {
    return null
  }

  async function handleEnable() {
    if (!userId) return

    try {
        setEnabling(true)

        console.log('[push] starting', {
        permission:
            typeof Notification !== 'undefined'
            ? Notification.permission
            : 'unavailable',
        standalone:
            window.matchMedia(
            '(display-mode: standalone)'
            ).matches,
        })

        const result =
        await enablePushNotifications({
            userId,
            sellerId,
        })

        console.log(
        '[push] enable result',
        result
        )

        if (result.ok) {
        setOpen(false)
        return
        }

        console.error(
        '[push] enable failed',
        result
        )
    } catch (error) {
        console.error(
        '[push] unexpected error',
        error
        )
    } finally {
        setEnabling(false)
    }
    }

  return (
    <div
      className="
        fixed
        inset-0
        z-[120]
        flex
        items-end
        justify-center
        bg-black/35
        backdrop-blur-sm
        animate-in
        fade-in
        duration-200
      "
    >
      <div
        className="
          w-full
          max-w-lg
          animate-in
          slide-in-from-bottom-10
          rounded-t-[2rem]
          bg-background
          px-6
          pb-[calc(1.5rem+env(safe-area-inset-bottom))]
          pt-4
          shadow-2xl
          duration-300
          sm:mb-6
          sm:rounded-[2rem]
        "
      >
        <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-muted-foreground/25" />

        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() =>
              setOpen(false)
            }
            className="h-8 w-8 rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mx-auto -mt-2 flex max-w-sm flex-col items-center text-center">
          <div
            className="
              flex
              h-16
              w-16
              items-center
              justify-center
              rounded-[1.4rem]
              bg-primary/10
              text-primary
            "
          >
            <Bell className="h-7 w-7" />
          </div>

          <h2 className="mt-5 text-xl font-semibold">
            Mantente al día
          </h2>

          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Recibe avisos de nuevos mensajes,
            eventos y actividad importante de
            tu comunidad.
          </p>

          <Button
            type="button"
            onClick={handleEnable}
            disabled={enabling}
            className="mt-7 h-12 w-full rounded-full"
          >
            {enabling
              ? 'Activando...'
              : 'Activar notificaciones'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              setOpen(false)
            }
            className="mt-2 rounded-full text-muted-foreground"
          >
            Ahora no
          </Button>
        </div>
      </div>
    </div>
  )
}